import React, { useState } from 'react';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Alert,
  Chip,
  Paper,
  Slider,
  MenuItem,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  MonetizationOn as PointsIcon,
  Inventory as InventoryIcon,
  Email as EmailIcon
} from '@mui/icons-material';
// Mock data removed - will fetch from API

const Settings = () => {
  const [settings, setSettings] = useState({
    lowStockThreshold: 10,
    autoReorder: true,
    emailNotifications: true,
    smsNotifications: false,
    lowStockAlerts: true
  }); // Default settings - will be fetched from API
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // TODO: Save to API
    setTimeout(() => {
      setSaveSuccess(true);
      setHasChanges(false);
    }, 500);
  };

  const handleReset = () => {
    setSettings({
      lowStockThreshold: 10,
      autoReorder: true,
      emailNotifications: true,
      smsNotifications: false,
      lowStockAlerts: true
    });
    setHasChanges(false);
  };

  const SettingsSection = ({ title, icon, children }) => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {React.cloneElement(icon, { sx: { mr: 2, color: '#5c725c' } })}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Breadcrumbs />
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          System Settings
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Configure your Bare Ordering system preferences
        </Typography>
      </Box>

      {/* Save Alert */}
      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have unsaved changes. Don't forget to save your settings.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>

          {/* Inventory Management */}
          <SettingsSection title="Inventory Management" icon={<InventoryIcon />}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" gutterBottom>
                  Low Inventory Threshold
                </Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={settings.lowInventoryThreshold}
                    onChange={(e, newValue) => handleSettingChange('lowInventoryThreshold', newValue)}
                    valueLabelDisplay="on"
                    min={5}
                    max={50}
                    marks={[
                      { value: 5, label: '5' },
                      { value: 20, label: '20' },
                      { value: 35, label: '35' },
                      { value: 50, label: '50' }
                    ]}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Alert when inventory falls below this quantity
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoFulfillment}
                      onChange={(e) => handleSettingChange('autoFulfillment', e.target.checked)}
                    />
                  }
                  label="Auto Fulfillment"
                />
                <Typography variant="body2" color="text.secondary">
                  Automatically assign serials and create shipping labels for single-item orders
                </Typography>
              </Grid>
            </Grid>
          </SettingsSection>

          {/* Notifications */}
          <SettingsSection title="Notifications" icon={<NotificationsIcon />}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    />
                  }
                  label="Email Notifications"
                />
                <Typography variant="body2" color="text.secondary">
                  Receive email alerts for low inventory, failed orders, and system updates
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notification Email"
                  type="email"
                  placeholder="admin@lipoderma.com"
                  helperText="Primary email address for system notifications"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
            </Grid>
          </SettingsSection>

          {/* Removed sections for a cleaner MVP: Points, Integrations, Security */}
        </Grid>

        {/* Settings Summary Sidebar */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 24 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Current Configuration
              </Typography>



              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Low Stock Alert
                </Typography>
                <Typography variant="body2">
                  Below {settings.lowInventoryThreshold} units
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Notifications
                </Typography>
                <Chip
                  label={settings.emailNotifications ? 'Enabled' : 'Disabled'}
                  color={settings.emailNotifications ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Auto Fulfillment
                </Typography>
                <Chip
                  label={settings.autoFulfillment ? 'Enabled' : 'Disabled'}
                  color={settings.autoFulfillment ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasChanges}
                  fullWidth
                >
                  Save Settings
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={handleReset}
                  disabled={!hasChanges}
                  fullWidth
                >
                  Reset Changes
                </Button>
              </Box>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>MVP Notice:</strong> These settings control UI behavior only.
                  Backend integration will be implemented in the production version.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        message="Settings saved successfully!"
      />
    </Box>
  );
};

export default Settings; 